import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { Box } from '@mui/material';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { LanguageSelectMultiple } from '../common/form/LanguageSelectMultiple';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { appTrainingLangListActions, getAppTrainingLangList, selectAppTrainingLangList } from '../../store/entities/appTrainingLangList';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { FetchState } from '../../store/fetchState';
import { selectBusinessSettings } from '../../store/entities/bussinesSettings';
import { getCompanySettings, selectCompanySettings, selectCompanySettingsState } from '../../store/entities/companySettings';
import { getCompanyCustomization } from '../../store/entities/companyCustomization';
import { CompanySettingsDto } from '@api/models/settings/CompanySettingsDto';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';
import { companySettingsService } from '@api/services/services';

export const CompSettTabLanguages: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const { applicationLanguages } = useAppSelector(selectBusinessSettings);
  const companySettings = useAppSelector(selectCompanySettings);

  useEffect(() => {
    dispatch(getCompanySettings());
  }, [dispatch, identityCompanyId]);

  useEffect(() => {
    dispatch(appTrainingLangListActions.reload(false));
  }, [dispatch, identityCompanyId]);

  useEffect(() => {
    if (appTrainingLangList.state === FetchState.None) {
      dispatch(getAppTrainingLangList());
    }
  }, [appTrainingLangList.state, dispatch]);

  const handleSubmit = async (data: CompanySettingsDto) => {
    await companySettingsService.updateSettings(data);

    dispatch(getCompanySettings());
    //The training languages and the default one are also reported by business-settings, which is what
    //the rest of the app reads them from.
    dispatch(getCompanyCustomization());
  };

  return (
    <Box mt={2}>
      <ShowResourceContent resource={resources.getSettings} fetchStateSelector={selectCompanySettingsState}>
        <FormikDebounce<CompanySettingsDto>
          initialValues={companySettings}
          enableReinitialize
          validationSchema={Yup.object().shape({
            defaultLanguage: validations.stringRequired(t),
            testLanguages: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <LanguageSelect
                name="defaultLanguage"
                label={t('admin.company.defaultApplicationLanguage')}
                languages={SUPPORTED_LANGUAGES}
                allowedLanguages={applicationLanguages}
                required
              />
              <LanguageSelectMultiple
                name="testLanguages"
                label={t('admin.company.testLanguages')}
                languages={appTrainingLangList.data?.content}
                required
              />
              <ShowError resource={resources.updateSettings} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
    </Box>
  );
};
